import React, { useMemo, useState } from 'react';
import {
  FiCalendar,
  FiClock,
  FiLink,
  FiHash,
  FiArrowRight,
} from 'react-icons/fi';
import {
  isArray,
  isEmpty,
  isPlainObject,
  isString,
  startCase,
  isNumber,
  isBoolean,
} from 'lodash';
import moment from 'moment-timezone';

import { SectionCard } from '@/components/organism/Cards/SectionCard';
import { StatCell } from '@/components/organism/Cards/StatCell';
import { NoDataPlate } from '@/components/organism/TableExpand/primitives';
import { Text } from '@/components/atoms/AppText';
import { MetaChip, Cell, PillCell } from '@/components/atoms';
import { getSectionIcon, getLabelIcon } from '@/components/atoms/IconMap';
import { APP_BG, APP_BORDER } from '@/constant/theme/colors.constant';
import { APP_HEX } from '@/constant/theme/hex.constant';
import { APP_LOG_PANEL_STYLES } from '@/constant/smartCooler/smartCooler.constant';
import SegmentedControl from '@/components/elements/SegmentedControl';
import { ORGANISM_PROPS } from '@/constant/global/global.constant';
import { getDateTime } from '@/utils/dateTime';
import { tid } from '@/utils/testId';
import { SMART_COOLER_TEST_IDS as TID } from '@/constant/testIds/smartCooler.testIds';

// ─── Types ────────────────────────────────────────────────────────────────────
interface AppLogExpandedPanelProps {
  meta?: Record<string, any> | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
const safeParse = (value: any): any => {
  if (!isString(value)) return value;
  const trimmed = value?.trim();
  if (isEmpty(trimmed)) return trimmed;
  try {
    const parsed = JSON.parse(trimmed);
    if (isPlainObject(parsed) || isArray(parsed)) return parsed;
    return trimmed;
  } catch {
    return trimmed;
  }
};

const deepParse = (value: any): any => {
  const parsed = safeParse(value);
  if (isArray(parsed)) return parsed.map((item) => deepParse(item));
  if (isPlainObject(parsed)) {
    return Object.entries(parsed).reduce(
      (acc, [k, v]) => {
        acc[k] = deepParse(v);
        return acc;
      },
      {} as Record<string, any>,
    );
  }
  return parsed;
};

const formatLabel = (key: string) => {
  if (key === 'fileName') return 'Filename';
  if (key === 'sizeLabel') return 'Original File Size';
  if (key === 'compressedSizeLabel') return 'Compressed File Size';
  if (key === 'captureRange') return 'Captured Range';
  return startCase(
    key
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .replace(/-/g, ' '),
  );
};

const ISO_DATE_RE =
  /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/;

const isDateLike = (v: any): boolean => {
  if (!isString(v)) return false;
  const t = v.trim();
  if (!ISO_DATE_RE.test(t)) return false;
  return moment(t, moment.ISO_8601, true).isValid();
};

const isUrlLike = (v: any): boolean =>
  isString(v) && /^https?:\/\/.+/i.test(v.trim());

// Matches "<iso> / <iso>" or "<iso> - <iso>" or "<iso> to <iso>" (case-insensitive)
const DATE_RANGE_RE = /^(.+?)\s*(?:\/|\s-\s|\bto\b)\s*(.+)$/i;

const parseRangeParts = (v: string): [string, string] | null => {
  const m = v.trim().match(DATE_RANGE_RE);
  if (!m) return null;
  const [, a, b] = m;
  if (isDateLike(a.trim()) && isDateLike(b.trim())) return [a.trim(), b.trim()];
  return null;
};

/**
 * Returns a modern ReactNode for primitive values:
 *  - ISO date range    → from → to chip with duration badge
 *  - ISO date/datetime → calendar chip + relative time
 *  - boolean           → coloured badge
 *  - number            → monospace chip
 *  - URL               → clickable link
 *  - plain string      → text
 */
const SmartValue = ({ value }: { value: any }) => {
  // ── null / undefined / empty ───────────────────────────────────────────
  if (value === null || value === undefined || value === '') {
    return (
      <Text as="span" size="md" color="faint" italic>
        —
      </Text>
    );
  }

  // ── boolean → PillCell with boolEnabled preset ────────────────────────
  if (isBoolean(value)) {
    return (
      <PillCell
        value={value ? 'ENABLED' : 'DISABLED'}
        preset="boolEnabled"
        dot
        shape="pill"
        size="text-[11px]"
        className="font-semibold border"
      />
    );
  }

  // ── number → MetaChip mono ────────────────────────────────────────────
  if (isNumber(value)) {
    return (
      <MetaChip mono>
        <FiHash size={9} className="opacity-40 shrink-0" />
        {value}
      </MetaChip>
    );
  }

  const str = String(value).trim();

  // ── ISO date range ("dateA / dateB" | "dateA - dateB" | "dateA to dateB") ──
  const rangeParts = parseRangeParts(str);
  if (rangeParts) {
    const [fromStr, toStr] = rangeParts;
    const fromHasTime = fromStr.includes('T');
    const toHasTime = toStr.includes('T');
    const fromFormatted = getDateTime(fromStr);
    const toFormatted = getDateTime(toStr);
    const diffDays = moment(toStr).diff(moment(fromStr), 'days');
    const diffLabel =
      diffDays === 0
        ? 'Same day'
        : diffDays === 1
          ? '1 day'
          : diffDays > 0
            ? `${diffDays} days`
            : `${Math.abs(diffDays)}d reversed`;
    return (
      <div className="flex flex-col gap-1 w-full max-w-full">
        <div className="flex flex-wrap items-center gap-1.5">
          {/* From chip */}
          <MetaChip className="bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-700 min-w-0 shrink">
            {fromHasTime ? (
              <FiClock size={10} className="shrink-0 opacity-60" />
            ) : (
              <FiCalendar size={10} className="shrink-0 opacity-60" />
            )}
            <span className="truncate">{fromFormatted}</span>
          </MetaChip>
          {/* Arrow — sits between chips, wraps naturally on small screens */}
          <FiArrowRight
            size={12}
            className="text-violet-400 dark:text-violet-500 shrink-0"
          />
          {/* To chip */}
          <MetaChip className="bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-700 min-w-0 shrink">
            {toHasTime ? (
              <FiClock size={10} className="shrink-0 opacity-60" />
            ) : (
              <FiCalendar size={10} className="shrink-0 opacity-60" />
            )}
            <span className="truncate">{toFormatted}</span>
          </MetaChip>
        </div>
        {/* Duration label */}
        <span className="inline-flex items-center gap-0.5 text-[9px] font-medium tracking-wide text-violet-700 dark:text-violet-300 pl-0.5">
          <FiClock size={8} className="shrink-0 opacity-70" />
          <span className="truncate">{diffLabel}</span>
        </span>
      </div>
    );
  }

  // ── ISO date / datetime ───────────────────────────────────────────────
  if (isDateLike(str)) {
    const hasTime = str.includes('T');
    const formatted = getDateTime(str);
    const relative = moment(str).fromNow();
    return (
      <div className="inline-flex flex-col gap-0.5">
        <MetaChip className="bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300 border-sky-200 dark:border-sky-700 rounded-md">
          {hasTime ? (
            <FiClock size={10} className="shrink-0 opacity-60" />
          ) : (
            <FiCalendar size={10} className="shrink-0 opacity-60" />
          )}
          {formatted}
        </MetaChip>
        <Text as="span" size="sm" color="subtle" className="pl-1">
          {relative}
        </Text>
      </div>
    );
  }

  // ── URL → inline link + Cell ──────────────────────────────────────────
  if (isUrlLike(str)) {
    return (
      <a
        href={str}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 hover:underline underline-offset-2 break-all"
      >
        <FiLink
          size={11}
          className="shrink-0 text-blue-500 dark:text-blue-400"
        />
        <Cell
          value={str}
          variant="primary"
          size="text-[11px]"
          className="break-all"
        />
      </a>
    );
  }

  // ── plain string → Cell ───────────────────────────────────────────────
  return (
    <Cell
      value={str}
      variant="default"
      size="text-[11px]"
      className="whitespace-pre-wrap break-words"
    />
  );
};

const displayValue = (v: any): React.ReactNode => <SmartValue value={v} />;

// ─── Source files chip stack ──────────────────────────────────────────────────
const SourceFilesChips = ({ value }: { value: string }) => {
  const files = value.split(',').map((f) => f.trim()).filter(Boolean);
  return (
    <div className="flex flex-col gap-1 w-full">
      {files.map((f, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 dark:bg-indigo-500 shrink-0" />
          <span className="font-mono text-[10.5px] text-gray-700 dark:text-gray-300 truncate">{f}</span>
        </div>
      ))}
    </div>
  );
};

// Keys to suppress at every level of the meta tree
const HIDDEN_META_KEYS = new Set(['sizeBytes', 'compressedSizeBytes']);

// Pinned display order for fileMeta keys (Row1 first 4, Row2 next 4, rest after)
const FILE_META_PIN_ORDER = [
  'fileName', 'sizeLabel', 'compressedSizeLabel', 'archivedAt',
  'requestedRange', 'captureRange', 'sourceFiles', 'uploadedAt',
];

const sortByPinOrder = (entries: [string, any][]): [string, any][] =>
  [...entries].sort(([a], [b]) => {
    const ai = FILE_META_PIN_ORDER.indexOf(a);
    const bi = FILE_META_PIN_ORDER.indexOf(b);
    if (ai === -1 && bi === -1) return 0;
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });

// ─── Recursive value renderer ─────────────────────────────────────────────────
const RenderValue = ({
  value,
  parentKey = '',
}: {
  value: any;
  parentKey?: string;
}) => {
  const parsed = deepParse(value);

  if (isArray(parsed)) {
    if (isEmpty(parsed)) {
      return (
        <Text as="span" size="md" color="faint" italic>
          No {formatLabel(parentKey)} found.
        </Text>
      );
    }
    return (
      <div className="flex flex-col gap-2">
        {parsed.map((item, idx) => (
          <div
            key={`${parentKey}-${idx}`}
            className={`relative overflow-hidden rounded-xl border ${APP_BORDER.soft} ${APP_BG.surface}`}
          >
            {/* Gradient top line */}
            <div
              className="h-[2px] w-full"
              style={{ background: APP_LOG_PANEL_STYLES.arrayItemTopLine }}
            />
            {/* Index badge */}
            <div
              className={`flex items-center gap-2 px-3 py-1.5 border-b ${APP_BORDER.soft}`}
              style={{ background: APP_LOG_PANEL_STYLES.arrayItemHeaderBg }}
            >
              <span
                className="inline-flex items-center justify-center h-4 w-4 rounded-full text-[9px] font-bold text-white"
                style={{ background: APP_LOG_PANEL_STYLES.arrayItemBadgeBg }}
              >
                {idx + 1}
              </span>
              <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                {parentKey ? `${formatLabel(parentKey)} Item` : `Item`} #
                {idx + 1}
              </span>
            </div>
            <div className="p-2">
              <RenderValue value={item} parentKey={`${parentKey}-${idx}`} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (isPlainObject(parsed)) {
    if (isEmpty(parsed)) {
      return (
        <Text as="span" size="md" color="faint" italic>
          No {formatLabel(parentKey)} found.
        </Text>
      );
    }
    return (
      <div
        className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 divide-x divide-y ${APP_BORDER.divideSoft}`}
      >
        {sortByPinOrder(Object.entries(parsed).filter(([key]) => !HIDDEN_META_KEYS.has(key)))
          .map(([key, val]) => (
            <StatCell
              key={key}
              label={formatLabel(key)}
              labelIcon={getLabelIcon(key)}
              value={
                isPlainObject(val) || isArray(val) ? (
                  <RenderValue value={val} parentKey={key} />
                ) : key === 'sourceFiles' && isString(val) ? (
                  <SourceFilesChips value={val} />
                ) : (
                  <SmartValue value={val} />
                )
              }
            />
          ))}
      </div>
    );
  }

  return <SmartValue value={parsed} />;
};

// ─── Main panel ───────────────────────────────────────────────────────────────
export const AppLogExpandedPanel = ({ meta }: AppLogExpandedPanelProps) => {
  const parsedMeta = useMemo(() => deepParse(meta ?? {}), [meta]);

  const { simpleEntries, objectEntries } = useMemo(() => {
    const simple: [string, any][] = [];
    const complex: [string, any][] = [];
    Object.entries(parsedMeta ?? {}).forEach(([key, value]) => {
      if (HIDDEN_META_KEYS.has(key)) return;
      if (isPlainObject(value) || isArray(value)) complex.push([key, value]);
      else simple.push([key, value]);
    });
    // Move any key containing "file" to the front of the complex (tab) list
    complex.sort((a, b) => {
      const aIsFile = /file/i.test(a[0]) ? -1 : 0;
      const bIsFile = /file/i.test(b[0]) ? -1 : 0;
      return aIsFile - bIsFile;
    });
    return { simpleEntries: simple, objectEntries: complex };
  }, [parsedMeta]);

  const tabs = useMemo(() => {
    return objectEntries.map(([key]) => ({
      value: key,
      label: formatLabel(key),
    }));
  }, [objectEntries]);

  const [activeTab, setActiveTab] = useState<string>(
    () => tabs[0]?.value ?? '',
  );
  const resolvedTab = tabs.find((t) => t.value === activeTab)
    ? activeTab
    : (tabs[0]?.value ?? '');

  if (isEmpty(simpleEntries) && isEmpty(objectEntries)) {
    return (
      <div className="p-4">
        <NoDataPlate message={ORGANISM_PROPS.APP_LOG_EXPANDED.NO_META} />
      </div>
    );
  }

  return (
    <div
      data-testid={tid(TID.APP_LOG_EXPANDED_PANEL)}
      className="flex flex-col gap-2.5 py-1"
    >
      {/* ── Segmented tab strip for nested sections ──────────────────── */}
      {tabs.length > 1 && (
        <div className="px-1 pt-0.5 flex justify-end">
          <SegmentedControl
            testId={tid(TID.APP_LOG_EXPANDED_TAB_CONTROL)}
            options={tabs}
            value={resolvedTab}
            onChange={(v) => setActiveTab(v)}
            size="sm"
            shape="rounded"
            variant="gradient"
          />
        </div>
      )}

      {/* ── Active nested section ────────────────────────────────────── */}
      {objectEntries
        .filter(([key]) => tabs.length <= 1 || resolvedTab === key)
        .map(([key, value]) => (
          <SectionCard
            key={`obj-${key}`}
            testId={tid(TID.APP_LOG_EXPANDED_SECTION(key))}
            title={formatLabel(key)}
            icon={getSectionIcon(key)}
            compact
            bodyClassName=""
          >
            <RenderValue value={value} parentKey={key} />
          </SectionCard>
        ))}
    </div>
  );
};
